// src/app/app.component.ts

import { Component, OnInit, HostListener, OnDestroy }         from '@angular/core';
import { Router, NavigationEnd }     from '@angular/router';
import { AuthService as Auth0Service } from '@auth0/auth0-angular'; 
import { ThemeService }              from './services/theme/theme.service';
import { LanguageService }           from './services/language/language.service';
import { RagService }                from './services/rag.service';
import { FrontendLoggerService }     from './services/frontend-logger.service';
import { AuthService }               from './services/auth/auth.service';
import { take, filter } from 'rxjs/operators';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  isAuthenticated    = false;
  isSidebarCollapsed = false;
  isOnline           = false;
  isMobile           = false;
  isSidebarOpenMobile = false;

  /**
   * Mismo corte que `md` en styles/_breakpoints.scss. El CSS aplica el layout
   * movil por debajo de 768 (`max-width: 767.98px`), asi que aqui la comparacion
   * tiene que ser estricta: con `<= 768` ambos lados discrepaban justo en 768px.
   */
  private static readonly MOBILE_BREAKPOINT = 768;
  // Optional reference placeholder for chart components that expose a reflow() API
  // This avoids compile errors when calling reflow() conditionally on resize.
  chartComponent?: { reflow?: () => void };

  constructor(
    private readonly auth0:    Auth0Service,
    private readonly router:   Router,
    public themeService: ThemeService,
    public languageService: LanguageService,
    private readonly ragService: RagService,
    private readonly frontendLogger: FrontendLoggerService,
    private readonly authService: AuthService // <--- inject custom AuthService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url = event.urlAfterRedirects;
        if (this.shouldPersistLastRoute(url)) {
          localStorage.setItem('lastRoute', url);
        }

        // En mobile, cerrar el sidebar tipo overlay tras navegar
        if (this.isMobile) {
          this.isSidebarOpenMobile = false;
          this.updateMobileBodyLock();
        }
      }
    });
  }

  ngOnInit(): void {
    this.isMobile = window.innerWidth < AppComponent.MOBILE_BREAKPOINT;
    this.frontendLogger.info('Inicializando aplicación', 'AppComponent.ngOnInit');

    // Espera a que el usuario esté autenticado
    this.auth0.isAuthenticated$.pipe(
      filter(isAuth => isAuth), // Solo si está autenticado
      take(1)
    ).subscribe(() => {
      if (this.isCurrentOAuthCallbackRoute()) {
        this.frontendLogger.debug('[AppComponent] Skipping post-auth navigation on OAuth provider callback route.');
        return;
      }

      if (this.isCurrentInviteOnboardingRoute()) {
        this.frontendLogger.debug('[AppComponent] Skipping post-auth navigation on invite onboarding route.');
        return;
      }

      // Registrar login exitoso solo una vez por sesión
      // Espera a que el perfil esté disponible
      this.auth0.user$.pipe(take(1)).subscribe(() => {
        // Llama al AuthService para registrar el login exitoso
        this.authService.registerLoginHistoryOnceIfNeeded();
      });
      // Procesa appState o lastRoute
      this.auth0.appState$.pipe(take(1)).subscribe(appState => {
        const target = appState?.target || localStorage.getItem('lastRoute') || '/chat';
        this.frontendLogger.info(`Callback Auth0: resolviendo flujo a partir de ${target}`, 'AppComponent.appState$');
        this.authService.routeAfterAuthentication(target, 'AppComponent.appState$');
      });
    });

    // resto de tu lógica…
    this.themeService.initializeTheme();
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved) this.isSidebarCollapsed = saved === 'true';
    this.ragService.getStatus().subscribe({
      next: r => this.isOnline = r.status === 'ok',
      error: () => this.isOnline = false
    });
  }

  /**
   * El menu movil es un overlay: sin esto solo se podia cerrar tocando el
   * backdrop, asi que con teclado no habia salida.
   */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (!this.isMobile || !this.isSidebarOpenMobile) {
      return;
    }
    this.isSidebarOpenMobile = false;
    this.updateMobileBodyLock();
  }

  @HostListener('window:resize')
  onResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < AppComponent.MOBILE_BREAKPOINT;
    if (wasMobile !== this.isMobile) {
      // Notificar a los charts que deben hacer reflow/redraw en cambios de breakpoint/orientación
      if (this.chartComponent && typeof this.chartComponent.reflow === 'function') {
        this.chartComponent.reflow();
      }
      // Si tienes múltiples charts, repite para cada uno o usa un servicio/evento.
    }
    if (!this.isMobile) {
      this.isSidebarOpenMobile = false;
    }
    this.updateMobileBodyLock();
  }

  toggleSidebar(): void {
    if (this.isMobile) {
      this.isSidebarOpenMobile = !this.isSidebarOpenMobile;
      this.updateMobileBodyLock();
    } else {
      this.isSidebarCollapsed = !this.isSidebarCollapsed;
      localStorage.setItem('sidebar_collapsed', this.isSidebarCollapsed.toString());
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('sidebar-mobile-open');
    document.body.style.overflow = '';
    document.body.style.touchAction = '';
  }

  isAuthRoute(): boolean {
    const locationUrl = `${globalThis.location?.pathname ?? ''}${globalThis.location?.search ?? ''}${globalThis.location?.hash ?? ''}`;
    return this.isAuthLayoutRoute(this.router.url) || this.isAuthLayoutRoute(locationUrl);
  }

  isCurrentOAuthCallbackRoute(): boolean {
    const locationUrl = `${globalThis.location?.pathname ?? ''}${globalThis.location?.search ?? ''}${globalThis.location?.hash ?? ''}`;
    return this.isOAuthCallbackRoute(this.router.url) || this.isOAuthCallbackRoute(locationUrl);
  }

  isCurrentInviteOnboardingRoute(): boolean {
    const locationUrl = `${globalThis.location?.pathname ?? ''}${globalThis.location?.search ?? ''}${globalThis.location?.hash ?? ''}`;
    return this.isInviteOnboardingRoute(this.router.url) || this.isInviteOnboardingRoute(locationUrl);
  }

  isAuthLayoutRoute(url: string): boolean {
    const parsed = this.parseUrl(url);
    if (!parsed) {
      return false;
    }

    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return path.toLowerCase() === '/login'
        || path.toLowerCase() === '/signup'
        || path.toLowerCase() === '/onboarding';
  }

  isOAuthCallbackRoute(url: string): boolean {
    const parsed = this.parseUrl(url);
    if (!parsed) {
      return false;
    }

    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return path === '/callback' || /^\/oauth\/[^/]+\/callback$/i.test(path);
  }

  isInviteOnboardingRoute(url: string): boolean {
    const parsed = this.parseUrl(url);
    if (!parsed) {
      return false;
    }

    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return path.toLowerCase() === '/onboarding';
  }

  shouldPersistLastRoute(url: string): boolean {
    const parsed = this.parseUrl(url);
    if (!parsed) {
      return false;
    }

    const path = parsed.pathname.toLowerCase();
    const forbidden = ['/login', '/signup', '/wizard', '/onboarding'];
    const hasReturnUrl = parsed.searchParams.has('returnUrl');

    if (forbidden.some(f => path.startsWith(f)) || hasReturnUrl) {
      return false;
    }

    if (path.includes('/callback') || path.startsWith('/oauth/')) {
      return false;
    }

    return !this.containsSensitiveOAuthParameter(parsed);
  }

  isChatRoute(): boolean {
    return this.router.url.startsWith('/chat');
  }

  private parseUrl(url: string): URL | null {
    try {
      return new URL(url, globalThis.location?.origin ?? 'http://localhost');
    } catch {
      return null;
    }
  }

  private containsSensitiveOAuthParameter(url: URL): boolean {
    const sensitiveKeys = ['code', 'state', 'access_token', 'refresh_token', 'id_token', 'client_secret'];
    if (sensitiveKeys.some(key => url.searchParams.has(key))) {
      return true;
    }

    const hash = this.decodeHash(url.hash);
    return sensitiveKeys.some(key => new RegExp(`(^|[?#&])${key}=`, 'i').test(hash));
  }

  private decodeHash(hash: string): string {
    try {
      return decodeURIComponent(hash);
    } catch {
      return hash;
    }
  }

  private updateMobileBodyLock(): void {
    const lock = this.isMobile && this.isSidebarOpenMobile;
    document.body.classList.toggle('sidebar-mobile-open', lock);
    document.body.style.overflow = lock ? 'hidden' : '';
    document.body.style.touchAction = lock ? 'none' : '';
  }

}
